import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { CHAMPIONS_LEAGUE_SETTINGS, MY_CHAMPIONS_LEAGUE_GAME_DATA, CHAMPIONS_LEAGUE_TEAMS } from '../../../graphql/queries';
import { shouldRejectEmptierServerRealResults } from '../../../utils/championsLeagueRealResults';
import { SAVE_CHAMPIONS_LEAGUE_GAME_DATA, UPDATE_CHAMPIONS_LEAGUE_GAME_DATA } from '../../../graphql/mutations';

export const useGameState = () => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [preseasonWinner, setPreseasonWinner] = useState('');
  const [leagueStanding, setLeagueStanding] = useState([]);
  const [playoffPredictions, setPlayoffPredictions] = useState({});
  const [knockoutPredictions, setKnockoutPredictions] = useState({});
  const [savedPlayoffPredictions, setSavedPlayoffPredictions] = useState({}); // Gemte predictions fra database
  const [savedKnockoutPredictions, setSavedKnockoutPredictions] = useState({}); // Gemte predictions fra database
  const [finalPrediction, setFinalPrediction] = useState('');
  const [points, setPoints] = useState(0);
  const [phase0Points, setPhase0Points] = useState(0);
  const [phase1Points, setPhase1Points] = useState(0);
  const [phase2Points, setPhase2Points] = useState(0);
  const [phase3Points, setPhase3Points] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [realResults, setRealResults] = useState({});
  const [teams, setTeams] = useState([]);
  const [savedGuesses, setSavedGuesses] = useState({
    phase0: false,
    phase1: false,
    phase2: false,
    phase2Playoff: false,
    phase2Round16: false,
    phase2Quarters: false,
    phase2Semis: false,
    phase3: false
  });
  
  // Confirmation modal state
  const [resetConfirmModal, setResetConfirmModal] = useState({ isOpen: false });

  // Hent fase låsning fra database via GraphQL
  const { data: settingsData, loading: settingsLoading, refetch: refetchSettings } = useQuery(CHAMPIONS_LEAGUE_SETTINGS);
  
  // Hent spil data fra database
  const { data: gameDataFromDB, loading: gameDataLoading, refetch: refetchGameData } = useQuery(MY_CHAMPIONS_LEAGUE_GAME_DATA);
  
  // Hent hold fra database
  const { data: teamsData, loading: teamsLoading, refetch: refetchTeams } = useQuery(CHAMPIONS_LEAGUE_TEAMS);
  const hydratedRef = useRef(false);
  
  // Mutations til at gemme spil data
  const [saveGameData] = useMutation(SAVE_CHAMPIONS_LEAGUE_GAME_DATA);
  const [updateGameData] = useMutation(UPDATE_CHAMPIONS_LEAGUE_GAME_DATA);
  
  // Hent fase låsning fra database eller brug default
  const phaseLocks = settingsData?.championsLeagueSettings?.phaseLocks || {
    phase0: false,
    phase1: false,
    phase2: false,
    phase2Playoff: false,
    phase2Round16: false,
    phase2Quarters: false,
    phase2Semis: false,
    phase3: false
  };

  const hasExistingGameData = !!gameDataFromDB?.myChampionsLeagueGameData;

  // Load teams from database - konverter Team objekter til strings
  useEffect(() => {
    if (teamsData?.championsLeagueTeams?.teams) {
      const teamNames = teamsData.championsLeagueTeams.teams.map(team => team.name);
      setTeams(teamNames);
    }
  }, [teamsData]);

  // Load game data from database on component mount
  useEffect(() => {
    if (gameDataLoading || settingsLoading || teamsLoading) {
      return;
    }

    if (gameDataFromDB?.myChampionsLeagueGameData) {
      const gameData = gameDataFromDB.myChampionsLeagueGameData;
      setPoints(gameData.points || 0);
      setPhase0Points(gameData.phase0Points || 0);
      setPhase1Points(gameData.phase1Points || 0);
      setPhase2Points(gameData.phase2Points || 0);
      setPhase3Points(gameData.phase3Points || 0);
      if (!hydratedRef.current) {
      setPreseasonWinner(gameData.preseasonWinner || '');
      setLeagueStanding(gameData.leagueStanding || []);
      
      // Parse JSON strings fra database til objekter
      let playoffPreds = {};
      let knockoutPreds = {};
      
      try {
        if (gameData.playoffPredictions) {
          if (typeof gameData.playoffPredictions === 'string') {
            // Håndter dobbelt-stringified JSON
            let parsed = gameData.playoffPredictions;
            while (typeof parsed === 'string') {
              try {
                parsed = JSON.parse(parsed);
              } catch (e) {
                // Hvis parsing fejler, brug tom object
                parsed = {};
                break;
              }
            }
            playoffPreds = parsed || {};
          } else {
            playoffPreds = gameData.playoffPredictions || {};
          }
        }
        
        if (gameData.knockoutPredictions) {
          if (typeof gameData.knockoutPredictions === 'string') {
            // Håndter dobbelt-stringified JSON
            let parsed = gameData.knockoutPredictions;
            while (typeof parsed === 'string') {
              try {
                parsed = JSON.parse(parsed);
              } catch (e) {
                // Hvis parsing fejler, brug tom object
                parsed = {};
                break;
              }
            }
            knockoutPreds = parsed || {};
          } else {
            knockoutPreds = gameData.knockoutPredictions || {};
          }
        }
      } catch (error) {
        console.error('Fejl ved parsing af predictions:', error);
        playoffPreds = {};
        knockoutPreds = {};
      }
      
      setPlayoffPredictions(playoffPreds);
      setKnockoutPredictions(knockoutPreds);
      // Gem også de originale gemte predictions for mismatch-detektion
      setSavedPlayoffPredictions(JSON.parse(JSON.stringify(playoffPreds))); // Deep copy
      setSavedKnockoutPredictions(JSON.parse(JSON.stringify(knockoutPreds))); // Deep copy
      setFinalPrediction(gameData.finalPrediction || '');
      setGameStarted(true);
      setCurrentPhase(0);
      
      // Opdater gemte gæt status baseret på database data
      // Et gæt er gemt hvis der er data, uanset om point er beregnet
      
      // Tjek om der faktisk er gyldige predictions (ikke bare tomme keys)
      const hasValidPlayoffPredictions = Object.keys(playoffPreds).length > 0 && 
        Object.values(playoffPreds).some(val => val && val !== '');
      const hasValidRound16Predictions = knockoutPreds.eighthFinals && 
        Object.keys(knockoutPreds.eighthFinals).length > 0 &&
        Object.values(knockoutPreds.eighthFinals).some(val => val && val !== '');
      const hasValidQuarterPredictions = knockoutPreds.quarterFinals && 
        Object.keys(knockoutPreds.quarterFinals).length > 0 &&
        Object.values(knockoutPreds.quarterFinals).some(val => val && val !== '');
      const hasValidSemiPredictions = knockoutPreds.semiFinals && 
        Object.keys(knockoutPreds.semiFinals).length > 0 &&
        Object.values(knockoutPreds.semiFinals).some(val => val && val !== '');
      
      setSavedGuesses({
        phase0: !!(gameData.preseasonWinner),
        phase1: !!(gameData.leagueStanding?.length === 36),
        phase2: !!(hasValidPlayoffPredictions || hasValidRound16Predictions || hasValidQuarterPredictions || hasValidSemiPredictions),
        phase2Playoff: hasValidPlayoffPredictions,
        phase2Round16: hasValidRound16Predictions,
        phase2Quarters: hasValidQuarterPredictions,
        phase2Semis: hasValidSemiPredictions,
        phase3: !!(gameData.finalPrediction)
      });
      }
      hydratedRef.current = true;
    } else if (!hydratedRef.current) {
      // Reset alle data hvis ingen database data - ikke fallback til localStorage
      // Dette sikrer at hver bruger starter med rent spil
      setPreseasonWinner('');
      setLeagueStanding([]);
      setPlayoffPredictions({});
      setKnockoutPredictions({});
      setFinalPrediction('');
      setPoints(0);
      setPhase0Points(0);
      setPhase1Points(0);
      setPhase2Points(0);
      setPhase3Points(0);
      setGameStarted(false);
      setCurrentPhase(0);
      setSavedGuesses({
        phase0: false,
        phase1: false,
        phase2: false,
        phase2Playoff: false,
        phase2Round16: false,
        phase2Quarters: false,
        phase2Semis: false,
        phase3: false
      });
      hydratedRef.current = true;
    }

    // Load real results from database
    if (settingsData?.championsLeagueSettings?.realResults) {
      const newRealResults = settingsData.championsLeagueSettings.realResults;
      setRealResults((prev) => {
        if (shouldRejectEmptierServerRealResults(newRealResults, prev)) {
          return prev;
        }
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(newRealResults);
        if (prevStr !== newStr) {
          return newRealResults;
        }
        return prev;
      });
    } else {
      // Fallback til localStorage hvis database ikke har data
      const savedRealResults = localStorage.getItem('championsLeagueRealResults');
      if (savedRealResults) {
        try {
          const parsed = JSON.parse(savedRealResults);
          setRealResults(prev => {
            const prevStr = JSON.stringify(prev);
            const newStr = JSON.stringify(parsed);
            if (prevStr !== newStr) {
              return parsed;
            }
            return prev;
          });
        } catch (e) {
          console.error('Fejl ved parsing af savedRealResults:', e);
        }
      }
    }
  }, [gameDataFromDB, settingsData, gameDataLoading, settingsLoading, teamsLoading]);

  // Save game data to localStorage whenever state changes (as backup)
  useEffect(() => {
    const gameData = {
      preseasonWinner,
      leagueStanding,
      playoffPredictions,
      knockoutPredictions,
      finalPrediction,
      points,
      phase0Points,
      phase1Points,
      phase2Points,
      phase3Points,
      gameStarted,
      currentPhase,
      savedGuesses
    };
    localStorage.setItem('championsLeagueGame', JSON.stringify(gameData));
  }, [preseasonWinner, leagueStanding, playoffPredictions, knockoutPredictions, finalPrediction, points, phase0Points, phase1Points, phase2Points, phase3Points, gameStarted, currentPhase, savedGuesses]);

  // Funktion til at gemme spil data i databasen
  const saveToDatabase = async (dataToSave) => {
    try {
      // Hvis der er eksisterende data, merge med nye data for at bevare eksisterende predictions
      let mergedPlayoffPredictions = dataToSave.playoffPredictions || {};
      let mergedKnockoutPredictions = dataToSave.knockoutPredictions || {};
      
      if (gameDataFromDB?.myChampionsLeagueGameData) {
        // Parse eksisterende data, håndter dobbelt-stringified JSON
        let existingPlayoff = {};
        let existingKnockout = {};
        
        try {
          if (gameDataFromDB.myChampionsLeagueGameData.playoffPredictions) {
            if (typeof gameDataFromDB.myChampionsLeagueGameData.playoffPredictions === 'string') {
              let parsed = gameDataFromDB.myChampionsLeagueGameData.playoffPredictions;
              while (typeof parsed === 'string') {
                try {
                  parsed = JSON.parse(parsed);
                } catch (e) {
                  parsed = {};
                  break;
                }
              }
              existingPlayoff = parsed || {};
            } else {
              existingPlayoff = gameDataFromDB.myChampionsLeagueGameData.playoffPredictions || {};
            }
          }
          
          if (gameDataFromDB.myChampionsLeagueGameData.knockoutPredictions) {
            if (typeof gameDataFromDB.myChampionsLeagueGameData.knockoutPredictions === 'string') {
              let parsed = gameDataFromDB.myChampionsLeagueGameData.knockoutPredictions;
              while (typeof parsed === 'string') {
                try {
                  parsed = JSON.parse(parsed);
                } catch (e) {
                  parsed = {};
                  break;
                }
              }
              existingKnockout = parsed || {};
            } else {
              existingKnockout = gameDataFromDB.myChampionsLeagueGameData.knockoutPredictions || {};
            }
          }
        } catch (error) {
          console.error('Fejl ved parsing af eksisterende predictions:', error);
          existingPlayoff = {};
          existingKnockout = {};
        }
        
        // Merge eksisterende med nye (nye overskriver eksisterende)
        mergedPlayoffPredictions = { ...existingPlayoff, ...mergedPlayoffPredictions };
        mergedKnockoutPredictions = { 
          ...existingKnockout, 
          ...mergedKnockoutPredictions,
          // Merge nested objects (eighthFinals, quarterFinals, semiFinals)
          eighthFinals: { ...existingKnockout.eighthFinals, ...mergedKnockoutPredictions.eighthFinals },
          quarterFinals: { ...existingKnockout.quarterFinals, ...mergedKnockoutPredictions.quarterFinals },
          semiFinals: { ...existingKnockout.semiFinals, ...mergedKnockoutPredictions.semiFinals }
        };
      }

      const gameData = {
        preseasonWinner: dataToSave.preseasonWinner !== undefined ? dataToSave.preseasonWinner : (gameDataFromDB?.myChampionsLeagueGameData?.preseasonWinner || ''),
        leagueStanding: dataToSave.leagueStanding && dataToSave.leagueStanding.length > 0 ? dataToSave.leagueStanding : (gameDataFromDB?.myChampionsLeagueGameData?.leagueStanding || []),
        playoffPredictions: JSON.stringify(mergedPlayoffPredictions),
        knockoutPredictions: JSON.stringify(mergedKnockoutPredictions),
        finalPrediction: dataToSave.finalPrediction !== undefined ? dataToSave.finalPrediction : (gameDataFromDB?.myChampionsLeagueGameData?.finalPrediction || ''),
        // VIGTIGT: points/phasePoints skal som udgangspunkt ikke sendes fra frontend,
        // da backend beregner dem og vi vil undgå at overskrive dem med 0.
        ...(dataToSave.points !== undefined ? { points: dataToSave.points } : {}),
        ...(dataToSave.phase0Points !== undefined ? { phase0Points: dataToSave.phase0Points } : {}),
        ...(dataToSave.phase1Points !== undefined ? { phase1Points: dataToSave.phase1Points } : {}),
        ...(dataToSave.phase2Points !== undefined ? { phase2Points: dataToSave.phase2Points } : {}),
        ...(dataToSave.phase3Points !== undefined ? { phase3Points: dataToSave.phase3Points } : {})
      };

      if (gameDataFromDB?.myChampionsLeagueGameData) {
        // Opdater eksisterende data
        await updateGameData({
          variables: { gameData }
        });
      } else {
        // Opret ny data
        await saveGameData({
          variables: { gameData }
        });
      }
      
      // Refetch data for at opdatere UI
      await refetchGameData();
      
      // Opdater gemte predictions når de bliver gemt
      if (dataToSave.playoffPredictions) {
        setSavedPlayoffPredictions(JSON.parse(JSON.stringify(dataToSave.playoffPredictions)));
      }
      if (dataToSave.knockoutPredictions) {
        setSavedKnockoutPredictions(JSON.parse(JSON.stringify(dataToSave.knockoutPredictions)));
      }
      
      // Opdater gemte gæt status
      if (dataToSave.preseasonWinner) {
        setSavedGuesses(prev => ({ ...prev, phase0: true }));
      }
      if (dataToSave.leagueStanding?.length === 36) {
        setSavedGuesses(prev => ({ ...prev, phase1: true }));
      }
      // Opdater phase 2 gruppe status hvis de er inkluderet i dataToSave
      if (dataToSave.phase2Group) {
        const groupKey = `phase2${dataToSave.phase2Group}`;
        setSavedGuesses(prev => ({ ...prev, [groupKey]: true, phase2: true }));
      }
      // Opdater phase 3 status hvis finalPrediction gemmes
      if (dataToSave.finalPrediction) {
        setSavedGuesses(prev => ({ ...prev, phase3: true }));
      }
    } catch (error) {
      console.error('Fejl ved gemning af spil data:', error);
      // Gem i localStorage som fallback
      localStorage.setItem('championsLeagueGame', JSON.stringify(dataToSave));
    }
  };

  const resetGame = () => {
    setResetConfirmModal({ isOpen: true });
  };

  const confirmResetGame = () => {
    setPreseasonWinner('');
    setLeagueStanding([]);
    setPlayoffPredictions({});
    setKnockoutPredictions({});
    setFinalPrediction('');
    setPoints(0);
    setPhase0Points(0);
    setPhase1Points(0);
    setPhase2Points(0);
    setPhase3Points(0);
    setGameStarted(false);
    setCurrentPhase(0);
    setSavedGuesses({
      phase0: false,
      phase1: false,
      phase2: false,
      phase2Playoff: false,
      phase2Round16: false,
      phase2Quarters: false,
      phase2Semis: false,
      phase3: false
    });
    localStorage.removeItem('championsLeagueGame');
    setResetConfirmModal({ isOpen: false });
  };

  const startGame = () => {
    setGameStarted(true);
    setCurrentPhase(0);
    // Gem i database når spillet startes
    saveToDatabase({
      preseasonWinner,
      leagueStanding,
      playoffPredictions,
      knockoutPredictions,
      finalPrediction,
      gameStarted: true,
      currentPhase: 0
    });
  };

  const nextPhase = () => {
    if (currentPhase < 3) {
      const nextPhaseNum = currentPhase + 1;
      const phaseKey = `phase${nextPhaseNum}`;
      if (!phaseLocks[phaseKey]) {
        setCurrentPhase(nextPhaseNum);
      }
    }
  };

  const prevPhase = () => {
    if (currentPhase > 0) {
      const prevPhaseNum = currentPhase - 1;
      const phaseKey = `phase${prevPhaseNum}`;
      if (!phaseLocks[phaseKey]) {
        setCurrentPhase(prevPhaseNum);
      }
    }
  };

  const refreshGame = useCallback(async () => {
    await Promise.all([
      refetchSettings(),
      refetchGameData(),
      refetchTeams(),
    ]);
  }, [refetchSettings, refetchGameData, refetchTeams]);

  return {
    // State
    currentPhase,
    preseasonWinner,
    leagueStanding,
    playoffPredictions,
    knockoutPredictions,
    savedPlayoffPredictions,
    savedKnockoutPredictions,
    finalPrediction,
    points,
    phase0Points,
    phase1Points,
    phase2Points,
    phase3Points,
    gameStarted,
    realResults,
    phaseLocks,
    hasExistingGameData,
    settingsLoading,
    gameDataLoading,
    teamsLoading,
    teams,
    savedGuesses,
    
    // Setters
    setCurrentPhase,
    setPreseasonWinner,
    setLeagueStanding,
    setPlayoffPredictions,
    setKnockoutPredictions,
    setFinalPrediction,
    setPoints,
    setPhase0Points,
    setPhase1Points,
    setPhase2Points,
    setPhase3Points,
    setGameStarted,
    setRealResults,
    
    // Actions
    resetGame,
    startGame,
    nextPhase,
    prevPhase,
    saveToDatabase,
    confirmResetGame,
    refreshGame,
    
    // Confirmation modals
    resetConfirmModal,
    setResetConfirmModal
  };
};
